import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/index";
import { PageContent } from "./ui/page-content";
import { PageHeading } from "./ui/page-heading";
import { useNavigate } from "react-router";
import { SocialLogin } from "@/components/ui/social-login";

export function Register() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showVerifyEmail, setShowVerifyEmail] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      const { error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            first_name: formData.firstName,
            last_name: formData.lastName,
          },
        },
      });

      if (signUpError) throw signUpError;

      setShowVerifyEmail(true);
    } catch (err: any) {
      setError(err.message);
    }
    setIsLoading(false);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  }

  if (showVerifyEmail) {
    return (
      <>
        <PageHeading>
          <div className="text-center">Verify your email</div>
        </PageHeading>
        <PageContent>
          <div className="space-y-4 max-w-lg mx-auto">
            <p>
              We have sent a verification email to{" "}
              <span className="font-bold">{formData.email}</span>
            </p>
            <p>
              Please click the link in the email to verify your account and
              continue.
            </p>
          </div>
        </PageContent>
      </>
    );
  }

  return (
    <>
      <PageHeading>
        <div className="text-center">Create an account</div>
      </PageHeading>
      <PageContent>
        <form onSubmit={handleSubmit} className="space-y-2 max-w-lg mx-auto">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First name</Label>
              <Input
                id="firstName"
                name="firstName"
                required
                value={formData.firstName}
                onChange={handleChange}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last name</Label>
              <Input
                id="lastName"
                name="lastName"
                required
                value={formData.lastName}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <PasswordInput
              id="password"
              name="password"
              required
              value={formData.password}
              onChange={handleChange}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm password</Label>
            <PasswordInput
              id="confirmPassword"
              name="confirmPassword"
              required
              value={formData.confirmPassword}
              onChange={handleChange}
            />
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
          <div className="pt-4 flex-col space-y-4">
            <Button
              type="submit"
              className="w-full"
              isLoading={isLoading}
              disabled={
                formData.password !== formData.confirmPassword ||
                !formData.firstName ||
                !formData.lastName ||
                !formData.email ||
                !formData.password
              }
            >
              Register
            </Button>
            <SocialLogin isLoading={isLoading} onError={setError} />
            <div className="text-center">
              <Button
                variant="link"
                onClick={() => navigate("/")}
                className="text-sm text-muted-foreground hover:text-primary"
              >
                Already have an account? Log in
              </Button>
            </div>
          </div>
        </form>
      </PageContent>
    </>
  );
}
